import { convertTitleToSlug } from '@cont/Content/FreeLesson/utils';
import {
  BLOCK_TYPE,
  DEFAULT_TABS,
  PHYSICAL_NOTES_CONTENT_TYPE_ID,
  TAB_TYPE_TO_BLOCK_TYPE
} from './constants';

const getDiscountPercentage = ({
  discountPercentage,
  sellingPrice,
  maxRetailPrice
}) => {
  if (discountPercentage != null)
    return Number.parseFloat(discountPercentage) || 0;
  if (!sellingPrice || !maxRetailPrice) return 0;

  return Math.floor(((maxRetailPrice - sellingPrice) / maxRetailPrice) * 100);
};

const getStatusCtaLabel = (status) =>
  status?.ctaLabel || status?.cta_label || null;

const normalizeDeliveryStatusDetails = (rawDeliveryStatus) => {
  let statuses = [];
  if (Array.isArray(rawDeliveryStatus)) {
    statuses = rawDeliveryStatus;
  } else if (rawDeliveryStatus) {
    statuses = [rawDeliveryStatus];
  }

  const details =
    statuses.find((status) => getStatusCtaLabel(status)) || statuses[0];

  if (!details) return null;

  const value = Number(details.value);
  return {
    title: details.title || null,
    ctaLabel: getStatusCtaLabel(details),
    value: Number.isNaN(value) ? details.value : value
  };
};

const getDeliveryStatusDetails = (physicalNotesDetails, note) => {
  const rawDeliveryStatus =
    physicalNotesDetails?.deliveryStatusDetails ??
    physicalNotesDetails?.deliveryStatus ??
    note?.deliveryStatusDetails ??
    note?.deliveryStatus ??
    null;

  return normalizeDeliveryStatusDetails(rawDeliveryStatus);
};

export const isPhysicalNotesContentType = (contentType, contentTypeId) =>
  contentType === 'Physical Notes' ||
  String(contentTypeId) === PHYSICAL_NOTES_CONTENT_TYPE_ID;

export const getPhysicalNotesSlug = (title) =>
  convertTitleToSlug(title || '') || '-';

export const mapPhysicalNotesContentBlocks = (physicalNotesDetails) => {
  const note = physicalNotesDetails?.notes?.[0] || {};
  const price = physicalNotesDetails?.price || {};
  const uid = physicalNotesDetails?.uid ?? null;
  const goal = physicalNotesDetails?.goal ?? null;
  const title =
    physicalNotesDetails?.name || physicalNotesDetails?.title || note?.title;
  const description =
    physicalNotesDetails?.description || note?.description || null;
  const portraitThumbnail =
    note?.thumbnail || physicalNotesDetails?.thumbnail || null;
  const landscapeThumbnail = physicalNotesDetails?.thumbnailV1 || null;
  const languages =
    note?.languages ||
    (note?.language
      ? [note.language]
      : physicalNotesDetails?.languages?.map(({ label }) => label)) ||
    [];
  const subjects = note?.subjects || [];
  const numberOfBooks = note?.numberOfBooks ?? null;
  const deliveryTimeText =
    physicalNotesDetails?.deliveryTimeText ||
    'Delivery to your home will take 12-15 days';
  const sellingPrice = price?.sellingPrice ?? null;
  const maxRetailPrice = price?.maxRetailPrice ?? null;
  const subscriptionUid =
    physicalNotesDetails?.subscriptionUid ||
    physicalNotesDetails?.skuUid ||
    uid;

  return [
    {
      blockType: BLOCK_TYPE.ABOUT,
      blockData: {
        uid,
        title,
        description,
        landscapeThumbnail,
        portraitThumbnail,
        goal,
        isSellable: physicalNotesDetails?.isSellable ?? true,
        contentTypeId:
          physicalNotesDetails?.contentTypeId ||
          Number(PHYSICAL_NOTES_CONTENT_TYPE_ID),
        valueProps: [
          numberOfBooks && {
            label: `${numberOfBooks} printed book${
              numberOfBooks === 1 ? '' : 's'
            } delivered to your home`,
            icon: 'store/notes/notesContainer.svg',
            scrollIcon: 'store/notes/notesContainer.svg'
          },
          languages.length && {
            label: `Available in ${languages.join(', ')}`,
            icon: 'store/notes/infoIcon.svg',
            scrollIcon: 'store/notes/infoIcon.svg'
          },
          {
            label: deliveryTimeText,
            icon: 'store/notes/deliveryStatus/shipped.svg',
            scrollIcon: 'store/notes/deliveryStatus/shipped.svg'
          }
        ].filter(Boolean),
        skuDetails: {
          uid: subscriptionUid,
          price: sellingPrice,
          currency: price?.currency || 'INR',
          maxRetailPrice,
          discountPercentage: getDiscountPercentage({
            discountPercentage: price?.discountPercentage,
            sellingPrice,
            maxRetailPrice
          }),
          priceEffectiveTill: price?.priceEffectiveTill ?? null,
          duration: physicalNotesDetails?.duration ?? null,
          displayDuration: physicalNotesDetails?.displayDuration ?? null
        }
      }
    },
    {
      blockType: BLOCK_TYPE.PHYSICAL,
      blockData: {
        uid: note?.uid || uid,
        goalUid: goal?.uid ?? null,
        goalSlug: goal?.slug ?? null,
        title: note?.title || title,
        description: note?.description || description,
        portraitThumbnail,
        landscapeThumbnail,
        valueOfferings:
          note?.valueOfferings ||
          subjects.map((subject) => `Covers ${subject}`),
        numberOfBooks,
        subjects,
        authors: note?.authors || [],
        languages,
        isbn13: note?.isbn13 || note?.isbn || null,
        yearOfPublication: note?.yearOfPublication ?? null,
        deliveryStatusDetails: getDeliveryStatusDetails(
          physicalNotesDetails,
          note
        )
      }
    }
  ];
};

export const buildPhysicalNotesPageBlocks = (
  physicalNotesDetails,
  faqBlockData
) => {
  const blocksByType = mapPhysicalNotesContentBlocks(
    physicalNotesDetails
  ).reduce(
    (blocks, block) => ({
      ...blocks,
      [block.blockType]: block
    }),
    {}
  );

  return (physicalNotesDetails?.tabs || DEFAULT_TABS)
    .map((tab) => {
      const tabType = tab.type || tab.blockType;
      const blockType =
        TAB_TYPE_TO_BLOCK_TYPE[tabType] ||
        TAB_TYPE_TO_BLOCK_TYPE[String(tabType).toLowerCase()] ||
        tabType;

      if (blockType === BLOCK_TYPE.FAQS) {
        return faqBlockData
          ? {
              blockType,
              blockData: faqBlockData,
              tabLabel: tab.title || tab.label
            }
          : null;
      }

      const block = blocksByType[blockType];
      return block ? { ...block, tabLabel: tab.title || tab.label } : null;
    })
    .filter(Boolean);
};
